import {
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { Column } from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import {
  DATA_TABLE_NOT_NULL_FILTER_VALUE,
  DATA_TABLE_NULL_FILTER_VALUE,
  DataTableColumnMeta,
  DataTableComparisonFilterOperator,
  DataTableComparisonFilterValue,
  DataTableFilterValue,
} from './DataTable.types'

interface DataTableColumnFilterProps<TData> {
  column: Column<TData, unknown>
  onClose: () => void
}

const DataTableColumnFilter = <TData,>({ column, onClose }: DataTableColumnFilterProps<TData>): React.ReactElement => {
  const t = useAppTranslations()
  const meta = column.columnDef.meta as DataTableColumnMeta<TData> | undefined
  const filterValue = meta?.onFilterChange ? meta.filterValue : column.getFilterValue()
  const isMultipleSelect = meta?.filterVariant === 'select' && meta.filterMultiple !== false
  const defaultComparisonOperator = meta?.filterVariant === 'number' ? 'moreThan' : 'before'
  const comparisonFilterValue =
    typeof filterValue === 'object' && filterValue !== null && !Array.isArray(filterValue)
      ? (filterValue as DataTableComparisonFilterValue)
      : undefined
  const [value, setValue] = useState<string | string[]>(
    Array.isArray(filterValue)
      ? filterValue.map((currentValue) => (currentValue === null ? DATA_TABLE_NULL_FILTER_VALUE : String(currentValue)))
      : filterValue === null
        ? isMultipleSelect
          ? [DATA_TABLE_NULL_FILTER_VALUE]
          : DATA_TABLE_NULL_FILTER_VALUE
        : filterValue === undefined || comparisonFilterValue
          ? isMultipleSelect
            ? []
            : ''
          : isMultipleSelect
            ? [String(filterValue)]
            : String(filterValue)
  )
  const [comparisonOperator, setComparisonOperator] = useState<DataTableComparisonFilterOperator>(
    comparisonFilterValue?.operator ?? defaultComparisonOperator
  )
  const [comparisonValue, setComparisonValue] = useState(String(comparisonFilterValue?.value ?? ''))
  const [comparisonStart, setComparisonStart] = useState(String(comparisonFilterValue?.start ?? ''))
  const [comparisonEnd, setComparisonEnd] = useState(String(comparisonFilterValue?.end ?? ''))

  const inputType = useMemo(() => {
    if (meta?.filterVariant === 'number') return 'number'
    if (meta?.filterVariant === 'date') return 'date'
    return 'text'
  }, [meta?.filterVariant])

  const parseFilterValue = (currentValue: string): string | number | boolean | null => {
    if (currentValue === DATA_TABLE_NULL_FILTER_VALUE) return null
    const option = meta?.filterOptions?.find((filterOption) => String(filterOption.value) === currentValue)
    return option ? option.value : currentValue
  }

  const getOptionLabel = (currentValue: string): string => {
    if (currentValue === DATA_TABLE_NULL_FILTER_VALUE) return t('table.emptyValues')
    return (
      meta?.filterOptions?.find((filterOption) => String(filterOption.value) === currentValue)?.label ?? currentValue
    )
  }

  const buildComparisonFilter = (): DataTableComparisonFilterValue | undefined => {
    if (comparisonOperator === 'between') {
      if (!comparisonStart || !comparisonEnd) return undefined
      return { end: comparisonEnd, operator: comparisonOperator, start: comparisonStart }
    }

    if (!comparisonValue) return undefined
    return { operator: comparisonOperator, value: comparisonValue }
  }

  const getUnrestrictedValue = (): DataTableFilterValue | undefined =>
    isMultipleSelect && meta?.filterOptions?.length ? meta.filterOptions.map((option) => option.value) : undefined

  const applyFilter = (): void => {
    const nextValue: DataTableFilterValue | undefined =
      meta?.filterVariant === 'date'
        ? buildComparisonFilter()
        : meta?.filterVariant === 'number'
          ? buildComparisonFilter()
          : Array.isArray(value)
            ? value.length > 0
              ? value.map(parseFilterValue)
              : undefined
            : value === DATA_TABLE_NULL_FILTER_VALUE
              ? null
              : value
                ? parseFilterValue(value)
                : undefined

    if (meta?.onFilterChange) meta.onFilterChange(nextValue ?? getUnrestrictedValue())
    else column.setFilterValue(nextValue)
    onClose()
  }

  const clearFilter = (): void => {
    setValue(isMultipleSelect ? [] : '')
    if (meta?.onFilterChange) meta.onFilterChange(getUnrestrictedValue())
    else column.setFilterValue(undefined)
    onClose()
  }

  const filterNullValues = (): void => {
    setValue(isMultipleSelect ? [DATA_TABLE_NULL_FILTER_VALUE] : DATA_TABLE_NULL_FILTER_VALUE)
    if (meta?.onFilterChange) meta.onFilterChange(null)
    else column.setFilterValue(DATA_TABLE_NULL_FILTER_VALUE)
    onClose()
  }

  const filterNotNullValues = (): void => {
    setValue(DATA_TABLE_NOT_NULL_FILTER_VALUE)
    column.setFilterValue(DATA_TABLE_NOT_NULL_FILTER_VALUE)
    onClose()
  }

  return (
    <Stack spacing={1.5} sx={{ minWidth: 220, p: 1.5 }}>
      {meta?.filterVariant === 'select' ? (
        <FormControl size="small">
          <InputLabel id={`data-table-filter-${column.id}`}>{t('table.filterValue')}</InputLabel>
          <Select
            input={<OutlinedInput label={t('table.filterValue')} />}
            labelId={`data-table-filter-${column.id}`}
            multiple={isMultipleSelect}
            onChange={(event) =>
              setValue(
                isMultipleSelect && typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : (event.target.value as string | string[])
              )
            }
            renderValue={(selected) =>
              Array.isArray(selected) ? selected.map(getOptionLabel).join(', ') : getOptionLabel(String(selected))
            }
            value={value}
          >
            {meta.filterOptions?.map((option) => {
              const optionValue = option.value === null ? DATA_TABLE_NULL_FILTER_VALUE : String(option.value)
              return (
                <MenuItem key={optionValue} value={optionValue}>
                  {isMultipleSelect && <Checkbox checked={Array.isArray(value) && value.includes(optionValue)} />}
                  <ListItemText primary={option.label} />
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      ) : meta?.filterVariant === 'date' || meta?.filterVariant === 'number' ? (
        <>
          <TextField
            label={t('table.comparisonFilter.operator')}
            onChange={(event) => setComparisonOperator(event.target.value as DataTableComparisonFilterOperator)}
            select
            size="small"
            value={comparisonOperator}
          >
            {meta.filterVariant === 'number'
              ? [
                  <MenuItem key="moreThan" value="moreThan">
                    {t('table.comparisonFilter.moreThan')}
                  </MenuItem>,
                  <MenuItem key="moreThanOrEqual" value="moreThanOrEqual">
                    {t('table.comparisonFilter.moreThanOrEqual')}
                  </MenuItem>,
                  <MenuItem key="lessThan" value="lessThan">
                    {t('table.comparisonFilter.lessThan')}
                  </MenuItem>,
                  <MenuItem key="lessThanOrEqual" value="lessThanOrEqual">
                    {t('table.comparisonFilter.lessThanOrEqual')}
                  </MenuItem>,
                ]
              : [
                  <MenuItem key="before" value="before">
                    {t('table.comparisonFilter.before')}
                  </MenuItem>,
                  <MenuItem key="after" value="after">
                    {t('table.comparisonFilter.after')}
                  </MenuItem>,
                ]}
            <MenuItem value="between">{t('table.comparisonFilter.between')}</MenuItem>
          </TextField>
          {comparisonOperator === 'between' ? (
            <Stack direction="row" spacing={1}>
              <TextField
                label={t('table.comparisonFilter.start')}
                onChange={(event) => setComparisonStart(event.target.value)}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type={inputType}
                value={comparisonStart}
              />
              <TextField
                label={t('table.comparisonFilter.end')}
                onChange={(event) => setComparisonEnd(event.target.value)}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type={inputType}
                value={comparisonEnd}
              />
            </Stack>
          ) : (
            <TextField
              label={t('table.filterValue')}
              onChange={(event) => setComparisonValue(event.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              type={inputType}
              value={comparisonValue}
            />
          )}
        </>
      ) : (
        <TextField
          label={t('table.filterValue')}
          onChange={(event) => setValue(event.target.value)}
          size="small"
          slotProps={inputType === 'date' ? { inputLabel: { shrink: true } } : undefined}
          type={inputType}
          value={value}
        />
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        {meta?.enableNullFilter && (
          <>
            <Button onClick={filterNullValues} size="small">
              {t('table.emptyValues')}
            </Button>
            {!meta.onFilterChange && (
              <Button onClick={filterNotNullValues} size="small">
                {t('table.nonEmptyValues')}
              </Button>
            )}
          </>
        )}
        <Button data-testid="data-table-clear-filter" onClick={clearFilter} size="small">
          {t('search.resetFilters')}
        </Button>
        <Button data-testid="data-table-apply-filter" onClick={applyFilter} size="small" variant="contained">
          {t('commons.actions.filter')}
        </Button>
      </Stack>
    </Stack>
  )
}

export default DataTableColumnFilter
